import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Checkbox,
  Col,
  Dropdown,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, SettingOutlined, EditOutlined, DeleteOutlined, ImportOutlined, ExportOutlined, FileOutlined } from "@ant-design/icons";
import ProductTypeModal from "./ProductTypeModal";
import ProductTypeForm from "./ProductTypeForm";
import {
  useGetProductTypesQuery,
  usePostProductTypeMutation,
  usePutProductTypeMutation,
  useDeleteProductTypeMutation,
  usePutProductTypeStatusMutation
} from "../../../../api/usim/uploads/products/productTypeApi";
import { UsimTypeItem } from "../../../../api/usim/uploads/products/usimTypeItem";
import UploadJobTable from "./UploadJobTable";
import handleUploadFile from "../../../../utils/uploadFileS3";
import { BASE_URL } from "../../../../api/emptyV2Api";

const { Title } = Typography;

const ENDPOINT = "usim/product-type/upload?key=input"

export default function ProductTypesPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form] = Form.useForm();

  // API hooks
  const { data: apiData, error, isFetching, refetch } = useGetProductTypesQuery();
  const [postProductType] = usePostProductTypeMutation();
  const [putProductType] = usePutProductTypeMutation();
  const [putProductTypeStatus] = usePutProductTypeStatusMutation();
  const [deleteProductType] = useDeleteProductTypeMutation();

  const [filteredData, setFilteredData] = useState<UsimTypeItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState<UsimTypeItem | null>(null);
  const [searchText, setSearchText] = useState("");
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const [showUploadJobs, setShowUploadJobs] = useState(false);
  const [isEditingModalVisible, setIsEditingModalVisible] = useState(false);
  const [editingProductType, setEditingProductType] = useState<UsimTypeItem | null>(null);


    // Upload progress states
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploadComplete, setIsUploadComplete] = useState(false);
    const [isUploadError, setIsUploadError] = useState(false);
    const [uploadFileName, setUploadFileName] = useState<string>("");

  // Transform API data to include keys for table
  const transformedData: UsimTypeItem[] = React.useMemo(() => {
    if (!apiData) return [];
    return apiData.map((item: UsimTypeItem, index: number) => ({
      key: `${item.name}-${index}`,
      id: item.id ?? "",
      name: item.name,
      iccid: item.iccid,
      status: item.status,
      activationDate: item.activationDate,
    }));
  }, [apiData]);

  // Update filtered data when API data changes
  useEffect(() => {
    setFilteredData(transformedData);
  }, [transformedData]);

  const showOptions = (show: boolean) => {
    setIsOptionsVisible(show);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // console.log("File selected:", file.name);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Initialize upload states
    resetUploadStates();
    setUploadFileName(file.name);
    setIsUploadModalVisible(true);

    try {
      // Simulate progress for user feedback
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90; // Keep at 90% until actual upload completes
          }
          return prev + 10;
        });
      }, 200);

      // Perform the actual upload
      await handleUploadFile(BASE_URL + ENDPOINT, file);

      // Clear interval and set to 100%
      clearInterval(progressInterval);
      setUploadProgress(100);
      setIsUploadComplete(true);

      message.success("Product plans file uploaded successfully");
    } catch (error) {
      console.error("Error uploading file:", error);
      setIsUploadError(true);
      setUploadProgress(0);
      message.error("Failed to upload file. Please try again.");
    }
  };

  // Reset upload states
  const resetUploadStates = () => {
    setUploadProgress(0);
    setIsUploadComplete(false);
    setIsUploadError(false);
    setUploadFileName("");
  };

  // Handle upload modal close
  const handleUploadModalClose = () => {
    setIsUploadModalVisible(false);
    resetUploadStates();
    if (isUploadComplete) {
      refetch(); // Refresh data if upload was successful
    }
  };
  // Handle import
  const handleImport = () => {
    fileInputRef.current?.click();
  };

  // Handle export
  const handleExport = async () => {
    await handleExportData();
  };

  const handleViewImportStatus = () => {
    // message.info("View import status functionality coming soon");
    setShowUploadJobs(true);
  };
  // Dropdown menu items
  const optionsMenuItems = [
    {
      key: "import",
      label: "Import",
      icon: <ImportOutlined />,
      onClick: handleImport,
    },
    {
      key: "export",
      label: "Export",
      icon: <ExportOutlined />,
      onClick: handleExport,
    },
    {
      key: "View Import Status",
      label: "View Import Status",
      icon: <FileOutlined />,
      onClick: handleViewImportStatus,
    },
  ];

  // Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredData(transformedData);
      return;
    }

    const filtered = transformedData.filter(item =>
      item.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  // Handle add new product type
  const handleAdd = () => {
    setIsModalVisible(true);
  };

  // Handle add form submit
  const handleAddSubmit = async (values: { name: string; iccid: boolean , activationDate?: boolean }) => {
    try {
      const newProductType: UsimTypeItem = {
        name: values.name,
        iccid: values.iccid,
        activationDate: values.activationDate ?? false,
      };

      await postProductType(newProductType).unwrap();
      setIsModalVisible(false);
      message.success("Product type added successfully");
      refetch(); // Refresh the data
    } catch (error: any) {
      console.error('Failed to add product type:', error);
      message.error(error?.data?.message);
    }
  };

  // Handle edit form submit
  const handleEditSubmit = async (values: { name: string; iccid: boolean, activationDate?: boolean }) => {
    try {
      if (editingProductType) {
        const updatedProductType: UsimTypeItem = {
          ...editingProductType,
          id: editingProductType.id,
          name: values.name,
          iccid: values.iccid,
          activationDate: values.activationDate ?? false,
        };
        await handleEdit(updatedProductType);
        setIsEditingModalVisible(false);
        setEditingProductType(null);
      }
    } catch (error) {
      console.error('Failed to edit product type:', error);
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  // Handle row selection
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // Handle row click to show details
  const handleRowClick = (record: UsimTypeItem) => {
    setSelectedProductType(record);
    setIsDetailModalVisible(true);
  };

  // Handle edit product type
  const handleEdit = async (productType: UsimTypeItem) => {
    try {

      const updatedItem: UsimTypeItem = {
        id: productType.id,
        name: productType.name,
        iccid: productType.iccid,
        status: productType.status,
        activationDate: productType.activationDate,
      };
      // console.log(updatedItem);

      await putProductType(updatedItem).unwrap();
      message.success("Product type updated successfully");
      refetch(); // Refresh the data
    } catch (error) {
      console.error('Failed to update product type:', error);
      message.error("Failed to update product type. Please try again.");
    }
  };

  // Handle delete product type
  const handleDelete = async (key: string) => {
    Modal.confirm({
      title: "Delete Product Type",
      content: "Are you sure you want to delete this product type?",
      onOk: async () => {
        const productToDelete = transformedData.find(item => item.id === key);
        if (!productToDelete) return;

        try {
          const deleteItem: UsimTypeItem = {
            id: productToDelete.id,
            name: productToDelete.name,
            iccid: productToDelete.iccid,
          };

          await deleteProductType(deleteItem).unwrap();
          setSelectedRowKeys(prev => prev.filter(k => k !== key));
          message.success("Product type deleted successfully");
          refetch(); // Refresh the data
        } catch (error) {
          console.error('Failed to delete product type:', error);
          message.error("Failed to delete product type. Please try again.");
        }
      },
    });

  };

  // Handle bulk edit
  const handleBulkEdit = () => {
    message.info("Bulk edit functionality coming soon");
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    Modal.confirm({
      title: "Delete Selected Items",
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected product type(s)?`,
      onOk: async () => {
        try {
          const deletePromises = selectedRowKeys.map(async (key) => {
            const productToDelete = transformedData.find(item => item.id === key);
            if (productToDelete) {
              const deleteItem: UsimTypeItem = {
                id: productToDelete.id,
                name: productToDelete.name,
                iccid: productToDelete.iccid,
              };
              return deleteProductType(deleteItem).unwrap();
            }
          });

          await Promise.all(deletePromises);
          setSelectedRowKeys([]);
          message.success(`${selectedRowKeys.length} product type(s) deleted successfully`);
          refetch(); // Refresh the data
        } catch (error) {
          console.error('Failed to delete product types:', error);
          message.error("Failed to delete some product types. Please try again.");
        }
      },
    });
  };

  const handleStatusChange = async (productType: UsimTypeItem) => {
    const updatedItem: UsimTypeItem = {
      id: productType.id,
      name: productType.name,
      iccid: productType.iccid,
      status: !productType.status,
    };
    // console.log(updatedItem);
    await putProductTypeStatus(updatedItem).unwrap();
    message.success("Product type status updated successfully");
    setIsDetailModalVisible(false);
    refetch(); // Refresh the data
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns: ColumnsType<UsimTypeItem> = [
    {
      title: "Product Type",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Product Type ID",
      dataIndex: "id",
      key: "id",

    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: boolean) => (
        <Tag color={status ? "green" : "red"}>{status ? "ACTIVE" : "INACTIVE"}</Tag>
      ),
    },
    {
      title: "ICCID",
      dataIndex: "iccid",
      key: "iccid",
      render: (hasICCID: boolean) => (
        <Checkbox checked={hasICCID} disabled />
      ),
    },
    {
      title: "Activation Date",
      dataIndex: "activationDate",
      key: "activationDate",
      render: (activationDate: boolean) => (
        <Checkbox checked={activationDate} disabled />
      ),
    },
     {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditingProductType(record);
              setIsEditingModalVisible(true);
            }}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record.id!);
            }}
          />
        </Space>
      ),
    },
  ];

  // Handle API errors
  if (error) {
    console.error('API Error:', error);
  }

  // Column mapping for export (customize export headers here)
  const exportColumnMapping = {
    Id: "Id",
    type: "Product Type",
    iccid: "ICCID",
    status: "Status"
  };

  // Handle data export
  const handleExportData = async () => {
    try {
      if (filteredData.length === 0) {
        message.warning("No data to export");
        return;
      }

      // console.log("filteredData", filteredData);
      // Prepare data for export
      const exportData = filteredData.map(item => ({
        [exportColumnMapping.Id]: item.id,
        [exportColumnMapping.type]: item.name,
        [exportColumnMapping.iccid]: item.iccid == true || item.iccid.toString().toLowerCase() == "true" ? "true" : "false",
        [exportColumnMapping.status]: item.status,
      }));

      // Convert to CSV format
      const headers = Object.values(exportColumnMapping);
      const csvContent = [
        headers.join(","), // Header row
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header] || "";
            // Escape quotes and wrap in quotes if contains comma or quote
            return value;
          }).join(",")
        )
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", `product-type-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Exported ${exportData.length} product type successfully`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export data. Please try again.");
    }
  };

  return (
    <Flex gap={16} vertical style={{ padding: "24px" }}>
      <Title level={2}>Product Types</Title>

         {/* Hidden file input for import functionality */}
         <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".csv,.xlsx,.xls"
        onChange={handleFileUpload}
      />
      {/* Header with search and buttons */}
      <Row justify="space-between" align="middle">
        <Col>
          {selectedRowKeys.length > 0 && (
            <Space>
              <Button
                icon={<EditOutlined />}
                onClick={handleBulkEdit}
              >
                Edit ({selectedRowKeys.length})
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDelete}
              >
                Delete ({selectedRowKeys.length})
              </Button>
              <Button icon={<SettingOutlined />}>
                Options
              </Button>
            </Space>
          )}
           {showUploadJobs && (
            <Button icon={<FileOutlined />} onClick={() => setShowUploadJobs(false)}>
              View Types 
            </Button>
          )}
        </Col>
        <Col>
          <Space>
            <Input.Search
              placeholder="Search product types"
              allowClear
              style={{ width: 250 }}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              value={searchText}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add
            </Button>
            <Dropdown
              menu={{ items: optionsMenuItems }}
              trigger={['hover']}
              open={isOptionsVisible}
              onOpenChange={showOptions}
            >
              <Button icon={<SettingOutlined />}>
                Options
              </Button>
            </Dropdown>

          </Space>
        </Col>
      </Row>

      {/* Table */}
          {/* Table */}
          {showUploadJobs && (
        <UploadJobTable/>
      )}
     {!showUploadJobs && (
      <Table
        rowKey="key"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredData}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
        pagination={{
          defaultPageSize: 10,
          pageSizeOptions: [5, 10, 20, 50],
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} records`,
        }}
         bordered
        size="small"
        loading={isFetching}
      />
      )}
      {/* Add Product Type Modal */}
      <Modal
        title="Add Product Type"
        open={isModalVisible}
        onOk={() => {
          // Trigger form submission
          const formElement = document.querySelector('#add-product-form form') as HTMLFormElement;
          formElement?.requestSubmit();
        }}
        onCancel={handleModalCancel}
        okText="Submit"
        cancelText="Cancel"
      >
        <div id="add-product-form">
          <ProductTypeForm
            isEdit={false}
            onSubmit={handleAddSubmit}
          />
        </div>
      </Modal>

      {/* Edit Product Type Modal */}
      <Modal
        title="Edit Product Type"
        open={isEditingModalVisible}
        onOk={() => {
          // Trigger form submission
          const formElement = document.querySelector('#edit-product-form form') as HTMLFormElement;
          formElement?.requestSubmit();
        }}
        onCancel={() => {
          setIsEditingModalVisible(false);
          setEditingProductType(null);
        }}
        okText="Save Changes"
        cancelText="Cancel"
      >
        <div id="edit-product-form">
          <ProductTypeForm
            isEdit={true}
            productType={editingProductType}
            onSubmit={handleEditSubmit}
          />
        </div>
      </Modal>

      {/* Product Type Details Modal */}
      <ProductTypeModal
        isVisible={isDetailModalVisible}
        onClose={() => setIsDetailModalVisible(false)}
        productType={selectedProductType}
        onStatusChange={handleStatusChange}
      />
    </Flex>
  );
}
